/**
 * localStorage, with a version on it.
 *
 * The version is the whole reason this is a module and not four lines inline.
 * Every system here is going to change shape — the setup grid, the BEO record,
 * the write-up file — and a save written by yesterday's build that loads into
 * today's produces a game that is subtly, silently wrong. Far better to refuse
 * it and start a clean day than to spend an evening debugging a ballroom whose
 * anchors moved.
 *
 * Reads are defensive to the point of paranoia because storage is not ours:
 * it can be full, disabled, cleared mid-session, or contain something a browser
 * extension put there. Any failure means "no save", never a crash on boot.
 */

export const SAVE_KEY = 'lionandcrown.save';
/*
 * 2: the player is a person now — a name, a body and a clothing slot. A save
 * from before that has nobody in it, and rather than invent one on load the
 * version drops it and the week starts again at the hiring screen.
 */
export const SAVE_VERSION = 2;

export function createSave({
  key = SAVE_KEY,
  version = SAVE_VERSION,
  storage = globalThis.localStorage,
} = {}) {
  let lastError = null;

  return {
    key,
    version,
    get available() {
      return Boolean(storage);
    },
    get lastError() {
      return lastError;
    },

    /** The saved state, or null. Never throws. */
    read() {
      lastError = null;
      if (!storage) return null;
      let raw = null;
      try {
        raw = storage.getItem(key);
      } catch (error) {
        lastError = `storage unreadable: ${error.message}`;
        return null;
      }
      if (!raw) return null;

      let parsed = null;
      try {
        parsed = JSON.parse(raw);
      } catch (error) {
        lastError = 'save is not valid JSON';
        return null;
      }
      if (!parsed || typeof parsed !== 'object') {
        lastError = 'save is not an object';
        return null;
      }
      if (parsed.version !== version) {
        lastError = `save is version ${parsed.version}, this build wants ${version}`;
        return null;
      }
      return parsed.state ?? null;
    },

    /** Returns true if it landed. A full quota is not a reason to stop playing. */
    write(state) {
      lastError = null;
      if (!storage) return false;
      try {
        storage.setItem(key, JSON.stringify({ version, savedAt: Date.now(), state }));
        return true;
      } catch (error) {
        lastError = `could not save: ${error.message}`;
        return false;
      }
    },

    clear() {
      try {
        storage?.removeItem(key);
        return true;
      } catch (error) {
        lastError = `could not clear: ${error.message}`;
        return false;
      }
    },
  };
}

/**
 * Debounced autosave.
 *
 * Every interesting moment in this game arrives in a burst — closing a BEO
 * files a record, moves four standings and clears the room, all in one frame —
 * and each of those wants to be saved. Writing four times is three wasted
 * serialisations of the whole world; waiting a beat and writing once is the
 * same result.
 */
export function createAutosave({ save, collect, delay = 0.8 }) {
  let pending = 0;
  let dirty = false;

  return {
    get dirty() {
      return dirty;
    },
    /** Marks the world as changed. Cheap; call it from anywhere. */
    touch() {
      dirty = true;
      pending = delay;
    },
    update(dt) {
      if (!dirty) return false;
      pending -= dt;
      if (pending > 0) return false;
      dirty = false;
      return save.write(collect());
    },
    /** Writes immediately, for the tab closing. */
    flush() {
      if (!dirty) return false;
      dirty = false;
      return save.write(collect());
    },
  };
}
